import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";
import { RESET_PASSWORD } from "src/constants/mutations";
import Logo from "../../components/common/Logo";
import { BtnLoader } from "../../Loader/index";

const MailIcon = () => (
  <svg className="w-[18px] h-[18px] text-[#9CA3AF] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [resetPassword, { data, loading, error }] = useMutation(RESET_PASSWORD);

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Must be a valid email")
        .required("Email is required"),
    }),
    onSubmit: (value) => {
      const httpOrHttps = window.location.href.split("//")[0];
      resetPassword({
        variables: {
          resetPasswordInput: {
            email: value.email,
            hostName: httpOrHttps + "//" + window.location.host,
          },
        },
      });
    },
  });

  const isSuccess = data?.resetPassword?.requestProcessed;
  const isButtonDisabled = !formik.isValid || !formik.dirty;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: "linear-gradient(180deg, #F7F8FA 0%, #EEF1F4 100%)" }}>
      <div
        onClick={() => navigate("/")}
        className="flex items-center justify-center gap-x-2 cursor-pointer mb-8"
      >
        <Logo />
        <h1 className="text-2xl font-semibold text-[#111827]">EatsBits</h1>
      </div>

      <div className="w-full max-w-[420px] rounded-[14px] px-8 pt-8 pb-5 bg-white shadow-[0_12px_30px_rgba(0,0,0,0.08)]">
        {isSuccess ? (
          <div className="text-center">
            <h2 className="text-[24px] font-bold text-[#111827]" style={{ letterSpacing: "-0.01em" }}>
              Check your email
            </h2>
            <p className="text-[15px] text-[#6B7280] mt-1.5 mb-5 max-w-[340px] mx-auto">
              If an account exists for that email, we&apos;ve sent a reset link.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-[10px] font-semibold text-[15px] text-white bg-[#FF6A3D] hover:bg-[#E85C32] hover:-translate-y-px active:translate-y-0 transition-all duration-150"
            >
              <ArrowLeftIcon />
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h2 className="text-[24px] font-bold text-[#111827]" style={{ letterSpacing: "-0.01em" }}>
              Reset your password
            </h2>
            <p className="text-[15px] text-[#6B7280] mt-1.5 mb-6 max-w-[340px]">
              Enter the email associated with your account. We&apos;ll send a reset link.
            </p>

            <form onSubmit={formik.handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#374151] mb-1.5">
                  Email
                </label>
                <div
                  className={`relative flex items-center h-[46px] pl-[44px] pr-4 rounded-[10px] border transition-all duration-150 ${
                    formik.touched.email && formik.errors.email
                      ? "border-red-400 bg-white shadow-[0_0_0_3px_rgba(239,68,68,0.15)]"
                      : "border-[#E5E7EB] bg-[#FAFAFA] focus-within:border-[#FF6A3D] focus-within:shadow-[0_0_0_3px_rgba(255,106,61,0.15)] focus-within:bg-white"
                  }`}
                >
                  <span className="absolute left-4 top-1/2 -translate-y-1/2">
                    <MailIcon />
                  </span>
                  <input
                    className="flex-1 text-sm text-black placeholder:text-[#9CA3AF] bg-transparent border-none outline-none focus:ring-0"
                    type="email"
                    name="email"
                    id="email"
                    placeholder="you@company.com"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.email}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-500 text-xs mt-1.5">{formik.errors.email}</p>
                )}
                {error && (
                  <p className="text-red-500 text-xs mt-1.5">{error.message}</p>
                )}
              </div>

              <div className="mt-6">
                {loading ? (
                  <div className="flex justify-center py-3">
                    <BtnLoader />
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={isButtonDisabled}
                    className={`w-full h-12 rounded-[10px] font-semibold text-[15px] text-white transition-all duration-150 ${
                      isButtonDisabled
                        ? "bg-[#FD7657] opacity-50 cursor-not-allowed"
                        : "bg-[#FF6A3D] hover:-translate-y-px hover:bg-[#E85C32] active:translate-y-0"
                    }`}
                  >
                    Send reset link
                  </button>
                )}
              </div>
            </form>

            <p className="text-center mt-5">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-[14px] text-[#6B7280] hover:text-[#FF6A3D] transition-colors duration-150 ease-in-out cursor-pointer"
              >
                <ArrowLeftIcon />
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};
